import { doc, getDoc, setDoc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { db } from '../firebase/config';
import { PointType, PointTypeManagementData, PointLevelSetDocument } from '../types/pointTypeManagement';

const convertDate = (date: any): Date => {
  if (!date) return new Date();
  if (date instanceof Date) return date;
  if (date.toDate && typeof date.toDate === 'function') return date.toDate();
  return new Date(date);
};

/** V2 듀얼 읽기: positiveTypes/negativeTypes (배열 또는 객체) → PointType[] */
function typesFieldToArray(raw: any): PointType[] {
  if (!raw) return [];
  let arr: any[];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === 'object') {
    try {
      arr = Array.from(Object.values(raw));
    } catch {
      return [];
    }
  } else {
    return [];
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((t: any) => t && typeof t === 'object')
    .map((type: any) => ({ ...type, createdAt: convertDate(type.createdAt), updatedAt: convertDate(type.updatedAt) }));
}

export class PointTypeManagementService {
  private userId: string;
  private classId: string;

  constructor(userId: string, classId: string) {
    this.userId = userId;
    this.classId = classId;
  }

  private getPointLevelSetDocRef() {
    return doc(db, `users/${this.userId}/classes/${this.classId}/setCollection/pointLevelSet`);
  }

  // 포인트 타입 데이터 로드
  async getPointTypeData(): Promise<PointTypeManagementData> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as any;
        const positiveTypes = typesFieldToArray(data.positiveTypes);
        const negativeTypes = typesFieldToArray(data.negativeTypes);
        return {
          positiveTypes,
          negativeTypes,
          levelUpPoints: data.levelUpPoints || 50,
          lastUpdated: data.lastUpdated ? convertDate(data.lastUpdated) : new Date()
        };
      } else {
        // 문서가 없으면 기본 데이터로 초기화
        await this.initializeDefaultPointTypes(1);
        return {
          positiveTypes: [],
          negativeTypes: [],
          levelUpPoints: 50,
          lastUpdated: new Date()
        };
      }
    } catch (error) {
      console.error('❌ 포인트 타입 데이터 로드 실패:', error);
      throw error;
    }
  }

  // 포인트 타입 생성
  async createPointType(pointType: Omit<PointType, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const id = `pointType_${Date.now()}`;
      const newPointType: PointType = {
        ...pointType,
        id,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      const rawData = docSnap.exists() ? docSnap.data() : null;
      const isObjectFormat = rawData && typeof rawData.positiveTypes === 'object' && !Array.isArray(rawData.positiveTypes);

      if (isObjectFormat) {
        const field = pointType.category === 'positive' ? 'positiveTypes' : 'negativeTypes';
        await updateDoc(docRef, {
          [`${field}.${id}`]: { ...newPointType, createdAt: newPointType.createdAt, updatedAt: newPointType.updatedAt },
          lastUpdated: serverTimestamp()
        });
        return id;
      }

      let currentData: PointLevelSetDocument;
      if (docSnap.exists()) {
        currentData = docSnap.data() as PointLevelSetDocument;
        const pos = typesFieldToArray((currentData as any).positiveTypes);
        const neg = typesFieldToArray((currentData as any).negativeTypes);
        currentData = { ...currentData, positiveTypes: pos, negativeTypes: neg };
      } else {
        currentData = {
          id: 'PointLevelSet',
          classId: this.classId,
          positiveTypes: [],
          negativeTypes: [],
          levelUpPoints: 50,
          lastUpdated: new Date()
        };
      }
      if (pointType.category === 'positive') {
        currentData.positiveTypes.push(newPointType);
      } else {
        currentData.negativeTypes.push(newPointType);
      }
      await setDoc(docRef, { ...currentData, lastUpdated: serverTimestamp() });
      return id;
    } catch (error) {
      console.error('❌ 포인트 타입 생성 실패:', error);
      throw error;
    }
  }

  // 포인트 타입 수정 (V2: dot notation 지원)
  async updatePointType(id: string, updates: Partial<Omit<PointType, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error('포인트 타입 문서가 존재하지 않습니다.');
      const rawData = docSnap.data() as any;
      const isObjectFormat = rawData && typeof rawData.positiveTypes === 'object' && !Array.isArray(rawData.positiveTypes);
      const positiveTypes = typesFieldToArray(rawData.positiveTypes);
      const negativeTypes = typesFieldToArray(rawData.negativeTypes);
      const inPositive = positiveTypes.find(p => p.id === id);
      const inNegative = negativeTypes.find(p => p.id === id);
      const field = inPositive ? 'positiveTypes' : inNegative ? 'negativeTypes' : null;
      if (!field) throw new Error('포인트 타입을 찾을 수 없습니다.');
      const existing = inPositive ?? inNegative!;
      const updated = { ...existing, ...updates, updatedAt: new Date() };

      if (isObjectFormat) {
        await updateDoc(docRef, {
          [`${field}.${id}`]: updated,
          lastUpdated: serverTimestamp()
        });
        return;
      }
      const currentData = docSnap.data() as PointLevelSetDocument & { positiveTypes: PointType[]; negativeTypes: PointType[] };
      const pos = typesFieldToArray(currentData.positiveTypes);
      const neg = typesFieldToArray(currentData.negativeTypes);
      if (inPositive) {
        const idx = pos.findIndex(p => p.id === id);
        if (idx !== -1) pos[idx] = updated;
      } else {
        const idx = neg.findIndex(p => p.id === id);
        if (idx !== -1) neg[idx] = updated;
      }
      await setDoc(docRef, { ...currentData, positiveTypes: pos, negativeTypes: neg, lastUpdated: serverTimestamp() });
    } catch (error) {
      console.error('❌ 포인트 타입 수정 실패:', error);
      throw error;
    }
  }

  // 포인트 타입 삭제 (V2: deleteField 지원)
  async deletePointType(id: string): Promise<void> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error('포인트 타입 문서가 존재하지 않습니다.');
      const rawData = docSnap.data() as any;
      const isObjectFormat = rawData && typeof rawData.positiveTypes === 'object' && !Array.isArray(rawData.positiveTypes);
      if (isObjectFormat) {
        const inPos = rawData.positiveTypes && rawData.positiveTypes[id];
        const field = inPos ? 'positiveTypes' : 'negativeTypes';
        await updateDoc(docRef, {
          [`${field}.${id}`]: deleteField(),
          lastUpdated: serverTimestamp()
        });
        return;
      }
      const positiveTypes = typesFieldToArray(rawData.positiveTypes).filter(p => p.id !== id);
      const negativeTypes = typesFieldToArray(rawData.negativeTypes).filter(p => p.id !== id);
      await setDoc(docRef, {
        ...rawData,
        positiveTypes,
        negativeTypes,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ 포인트 타입 삭제 실패:', error);
      throw error;
    }
  }

  // 포인트 타입 순서 변경 (V2: 객체일 때 { [id]: type } 전체 교체)
  async reorderPointTypes(category: 'positive' | 'negative', reorderedTypes: PointType[]): Promise<void> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error('포인트 타입 문서가 존재하지 않습니다.');
      const rawData = docSnap.data() as any;
      const isObjectFormat = rawData && typeof rawData.positiveTypes === 'object' && !Array.isArray(rawData.positiveTypes);
      const field = category === 'positive' ? 'positiveTypes' : 'negativeTypes';
      if (isObjectFormat) {
        const obj: Record<string, any> = {};
        for (const t of reorderedTypes) obj[t.id] = t;
        await updateDoc(docRef, { [field]: obj, lastUpdated: serverTimestamp() });
        return;
      }
      const currentData = rawData;
      const updated = category === 'positive'
        ? { ...currentData, positiveTypes: reorderedTypes }
        : { ...currentData, negativeTypes: reorderedTypes };
      await setDoc(docRef, { ...updated, lastUpdated: serverTimestamp() });
    } catch (error) {
      console.error('❌ 포인트 타입 순서 변경 실패:', error);
      throw error;
    }
  }

  // 기본 포인트 타입 초기화
  async initializeDefaultPointTypes(defaultPointValue: number): Promise<void> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const existingData = docSnap.data() as any;
        const posLen = Array.isArray(existingData.positiveTypes) ? existingData.positiveTypes.length : Object.keys(existingData.positiveTypes || {}).length;
        const negLen = Array.isArray(existingData.negativeTypes) ? existingData.negativeTypes.length : Object.keys(existingData.negativeTypes || {}).length;
        if (posLen > 0 || negLen > 0) return;
      }

      // positiveTypes는 빈 배열로 초기화 (사용자가 직접 추가)
      const defaultPositiveTypes: PointType[] = [];

      // negativeTypes는 기본값 유지
      const defaultNegativeTypes: PointType[] = [
        {
          id: `pointType_${Date.now()}_4`,
          points: defaultPointValue,
          memo: '주의집중 미흡',
          category: 'negative',
          createdAt: new Date(),
          updatedAt: new Date()
        },
        {
          id: `pointType_${Date.now()}_5`,
          points: defaultPointValue,
          memo: '방해행동',
          category: 'negative',
          createdAt: new Date(),
          updatedAt: new Date()
        },
        {
          id: `pointType_${Date.now()}_6`,
          points: defaultPointValue,
          memo: '파괴행동',
          category: 'negative',
          createdAt: new Date(),
          updatedAt: new Date()
        }
      ];

      // PointLevelSet 문서 생성
      const pointLevelSetDoc: PointLevelSetDocument = {
        id: 'PointLevelSet',
        classId: this.classId,
        positiveTypes: defaultPositiveTypes,
        negativeTypes: defaultNegativeTypes,
        levelUpPoints: 50,
        lastUpdated: new Date()
      };

      await setDoc(docRef, {
        ...pointLevelSetDoc,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ 기본 포인트 타입 초기화 실패:', error);
      throw error;
    }
  }

  // 레벨업 포인트 단위 업데이트
  async updateLevelUpPoints(levelUpPoints: number): Promise<void> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      
      let currentData: PointLevelSetDocument;
      if (docSnap.exists()) {
        currentData = docSnap.data() as PointLevelSetDocument;
      } else {
        // 문서가 없으면 기본 데이터로 초기화
        await this.initializeDefaultPointTypes(1);
        const newDocSnap = await getDoc(docRef);
        currentData = newDocSnap.data() as PointLevelSetDocument;
      }

      // 레벨업 포인트 단위 업데이트
      currentData.levelUpPoints = levelUpPoints;

      // 문서 업데이트
      await setDoc(docRef, {
        ...currentData,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ 레벨업 포인트 단위 업데이트 실패:', error);
      throw error;
    }
  }

  // 레벨별 아이템 카드 로드
  async getLevelItemCards(): Promise<{ [level: number]: string[] }> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as PointLevelSetDocument;
        return (data as any).levelItemCards || {};
      }
      return {};
    } catch (error) {
      console.error('❌ 레벨별 아이템 카드 로드 실패:', error);
      return {};
    }
  }

  // 레벨별 아이템 카드 업데이트
  async updateLevelItemCards(levelItemCards: { [level: number]: string[] }): Promise<void> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      
      let currentData: any;
      if (docSnap.exists()) {
        currentData = docSnap.data();
      } else {
        // 문서가 없으면 기본 데이터로 초기화
        await this.initializeDefaultPointTypes(1);
        const newDocSnap = await getDoc(docRef);
        currentData = newDocSnap.data();
      }

      // 레벨별 아이템 카드 업데이트
      currentData.levelItemCards = levelItemCards;

      // 문서 업데이트
      await setDoc(docRef, {
        ...currentData,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ 레벨별 아이템 카드 업데이트 실패:', error);
      throw error;
    }
  }

  // 특정 레벨의 아이템 카드 가져오기
  async getItemCardsForLevel(level: number): Promise<string[]> {
    try {
      const levelItemCards = await this.getLevelItemCards();
      return levelItemCards[level] || [];
    } catch (error) {
      console.error(`❌ 레벨 ${level} 아이템 카드 로드 실패:`, error);
      return [];
    }
  }

  // 레벨별 코인 로드
  async getLevelCoins(): Promise<{ [level: number]: number }> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as any;
        return data.levelCoins || {};
      }
      return {};
    } catch (error) {
      console.error('❌ 레벨별 코인 로드 실패:', error);
      return {};
    }
  }

  // 레벨별 코인 업데이트
  async updateLevelCoins(levelCoins: { [level: number]: number }): Promise<void> {
    try {
      const docRef = this.getPointLevelSetDocRef();
      const docSnap = await getDoc(docRef);
      
      let currentData: any;
      if (docSnap.exists()) {
        currentData = docSnap.data();
      } else {
        await this.initializeDefaultPointTypes(1);
        const newDocSnap = await getDoc(docRef);
        currentData = newDocSnap.data();
      }

      currentData.levelCoins = levelCoins;

      await setDoc(docRef, {
        ...currentData,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ 레벨별 코인 업데이트 실패:', error);
      throw error;
    }
  }
}

export default PointTypeManagementService;
